import { useState, useEffect, useCallback } from 'react';
import { ShotComment } from '../../../types';
import { listShotComments, createShotComment, resolveShotComment } from '../../../api';
import CommentComposer from '../../../components/CommentComposer';
import ImageThumbnail from '../../../components/ImageThumbnail';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface ShotCommentModalProps {
  shotId: string;
  shotIndex: number;
  t: Translate;
  onClose: () => void;
  onChanged: () => void;
}

export default function ShotCommentModal({ shotId, shotIndex, t, onClose, onChanged }: ShotCommentModalProps) {
  const [comments, setComments] = useState<ShotComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [changed, setChanged] = useState(false);

  const fetchComments = useCallback(async () => {
    try {
      const data = await listShotComments(shotId);
      setComments(data);
    } finally {
      setLoading(false);
    }
  }, [shotId]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const handleSubmit = async ({ content, imageUrls }: { content: string; imageUrls: string[] }) => {
    const created = await createShotComment(shotId, content, imageUrls);
    setComments(prev => [...prev, created]);
    setChanged(true);
  };

  const handleResolve = async (commentId: string) => {
    try {
      const updated = await resolveShotComment(commentId);
      setComments(prev => prev.map(c => c.id === commentId ? updated : c));
      setChanged(true);
    } catch { /* ignore */ }
  };

  const handleClose = () => {
    if (changed) onChanged();
    onClose();
  };

  return (
    <>
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000 }}
        onClick={handleClose}
      />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '960px', maxWidth: '90vw', maxHeight: '80vh',
        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px',
        display: 'flex', flexDirection: 'column', zIndex: 1001, boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      }}>
        {/* Header */}
        <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 600 }}>{t('comments.title', { index: shotIndex, count: comments.length })}</span>
          <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--text-2)', padding: '0.25rem' }}>✕</button>
        </div>

        {/* Comment List */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: 'var(--text-3)', padding: '2rem' }}>{t('comments.loading')}</div>
          ) : comments.length === 0 ? (
            <div style={{ textAlign: 'center', color: 'var(--text-3)', padding: '2rem' }}>{t('comments.empty')}</div>
          ) : (
            comments.map(comment => (
              <div
                key={comment.id}
                style={{
                  marginBottom: '1rem',
                  borderLeft: `3px solid ${comment.isResolved ? 'var(--status-completed, #22c55e)' : 'var(--status-failed, #ef4444)'}`,
                  paddingLeft: '0.75rem',
                  opacity: comment.isResolved ? 0.6 : 1,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{comment.username}</span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-3)' }}>
                    {new Date(comment.createdAt).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div style={{ fontSize: '0.88rem', background: 'var(--surface-2)', padding: '0.5rem 0.75rem', borderRadius: '6px', marginBottom: '0.35rem', wordBreak: 'break-word' }}>
                  {comment.content}
                </div>
                {(comment.images ?? []).length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: '0.35rem' }}>
                    {(comment.images ?? []).map((u, i) => <ImageThumbnail key={i} url={u} />)}
                  </div>
                )}
                {comment.isResolved ? (
                  <div style={{ fontSize: '0.75rem', color: 'var(--status-completed, #22c55e)' }}>
                    ✓ {comment.resolvedByName} {t('comments.resolved')} · {new Date(comment.updatedAt).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </div>
                ) : (
                  <button
                    onClick={() => handleResolve(comment.id)}
                    style={{
                      fontSize: '0.75rem', padding: '0.25rem 0.6rem',
                      background: 'rgba(34,197,94,0.1)', color: '#16a34a',
                      border: '1px solid rgba(34,197,94,0.3)', borderRadius: '4px',
                      cursor: 'pointer', fontWeight: 500,
                    }}
                  >
                    {t('comments.markResolved')}
                  </button>
                )}
              </div>
            ))
          )}
        </div>

        {/* Input */}
        <CommentComposer placeholder={t('comments.placeholder')} onSubmit={handleSubmit} />
      </div>
    </>
  );
}
